import { Button, Card, CheckBox } from '@toggle/design-system';
import { type PromptRefinementAngle } from '@toggle/toggle';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useRows } from '~/components/chat';

import styles from './PromptAngleCard.module.scss';
import { getLensColorVar } from './utils/getLensColorVar';

export interface PromptAngleCardProps {
  angle: PromptRefinementAngle;
  isOpen: boolean;
  isSelected: boolean;
  showSelection: boolean;
  onSelectPrompt: (prompt: string) => void;
  onSelectionChange: (isSelected: boolean) => void;
  onSuggestionEdited?: () => void;
  onPromptChange?: (prompt: string) => void;
}

export const PromptAngleCard = ({
  angle,
  isOpen,
  isSelected,
  showSelection,
  onSelectPrompt,
  onSelectionChange,
  onSuggestionEdited,
  onPromptChange,
}: PromptAngleCardProps) => {
  const { t } = useTranslation('chat');
  const [isEditing, setIsEditing] = useState(false);
  const [displayedPrompt, setDisplayedPrompt] = useState(angle.prompt);
  const [draftPrompt, setDraftPrompt] = useState(angle.prompt);
  const editInputRef = useRef<HTMLTextAreaElement>(null);
  const { rows, updateRows } = useRows({
    value: isEditing ? draftPrompt : '',
    textAreaRef: editInputRef,
    textAreaLineHeight: 20,
    compactMode: true,
  });

  useEffect(() => {
    setIsEditing(false);
    setDisplayedPrompt(angle.prompt);
    setDraftPrompt(angle.prompt);
  }, [isOpen, angle.prompt]);

  useEffect(() => {
    if (!isEditing) {
      return;
    }

    const el = editInputRef.current;

    if (!el) {
      return;
    }

    el.focus();
    const end = el.value.length;
    el.setSelectionRange(end, end);
  }, [isEditing]);

  const handleStartEdit = () => {
    setDraftPrompt(displayedPrompt);
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setDraftPrompt(displayedPrompt);
    setIsEditing(false);
  };

  const handleApplyEdit = () => {
    const trimmed = draftPrompt.trim();

    if (!trimmed || trimmed === displayedPrompt.trim()) {
      return;
    }

    setDisplayedPrompt(trimmed);
    setDraftPrompt(trimmed);
    setIsEditing(false);
    onSuggestionEdited?.();
    onPromptChange?.(trimmed);
  };

  const trimmedDraft = draftPrompt.trim();
  const isUpdateDisabled =
    !trimmedDraft || trimmedDraft === displayedPrompt.trim();
  const selectionLabel = t('chat:promptRefinement.selectPrompt');
  const selectionName = `prompt-refinement-${angle.lens}-${angle.label}`
    .toLowerCase()
    .replace(/\s+/g, '-');

  const handleSelectionChange = (
    _: string,
    event: ChangeEvent<HTMLInputElement>
  ) => {
    onSelectionChange(event.target.checked);
  };

  return (
    <Card className={styles.card}>
      <div className={styles.cardHeader}>
        <div>
          <p
            className={styles.cardEyebrow}
            style={{ color: getLensColorVar(angle.lens) }}
          >
            {angle.lens}
          </p>
          <h5 className={styles.cardTitle}>{angle.label}</h5>
        </div>
        <div className={styles.cardMeta}>
          <span className={styles.cardMetaText}>{angle.depth}</span>
          {showSelection && (
            <CheckBox
              className={styles.selectionCheckbox}
              name={selectionName}
              checked={isSelected}
              onChange={handleSelectionChange}
              label={
                <span className={styles.selectionLabel}>{selectionLabel}</span>
              }
              size="small"
              testId={selectionName}
            />
          )}
        </div>
      </div>
      {isEditing ? (
        <textarea
          ref={editInputRef}
          className={styles.editInput}
          value={draftPrompt}
          onChange={e => {
            updateRows(e);
            setDraftPrompt(e.target.value);
          }}
          rows={rows}
          aria-label={t('chat:promptRefinement.editPrompt')}
          data-testid="prompt-refinement-angle-edit-input"
        />
      ) : (
        <p className={styles.cardPrompt}>{displayedPrompt}</p>
      )}
      <div className={styles.cardAction}>
        {isEditing ? (
          <>
            <Button
              type="button"
              variant="tertiary"
              size="small"
              label={t('chat:promptRefinement.cancel')}
              onClick={handleCancelEdit}
            />
            <Button
              type="button"
              variant="secondary"
              size="small"
              label={t('chat:promptRefinement.update')}
              onClick={handleApplyEdit}
              disabled={isUpdateDisabled}
            />
          </>
        ) : (
          <>
            <Button
              type="button"
              variant="secondary"
              size="small"
              label={t('chat:promptRefinement.editPrompt')}
              onClick={handleStartEdit}
            />
            <Button
              type="button"
              variant="primary"
              size="small"
              label={t('chat:promptRefinement.usePrompt')}
              onClick={() => onSelectPrompt(displayedPrompt)}
            />
          </>
        )}
      </div>
    </Card>
  );
};
